<template>
  <v-card flat class="calibration-card">
    <v-card-title class="tab-content__title">
      {{ $t('map.deviceSettings.calibrationFactors.title') }}
    </v-card-title>
    <v-card-subtitle class="text-wrap">
      <i18n-t
        keypath="map.deviceSettings.calibrationFactors.subtitle"
        tag="div"
        class="hint-text"
        scope="global"
      >
        <a @click="navigateFaq">
          {{ $t('map.deviceSettings.calibrationFactors.faq') }}
        </a>
      </i18n-t>
    </v-card-subtitle>
    <v-row class="calibration-header">
      <v-col :cols="columnWidth"></v-col>
      <v-col :cols="columnWidth" class="multi-factor column-header">
        <span>
          {{ $t('map.deviceSettings.calibrationFactors.multiFactor') }}
        </span>
      </v-col>
      <v-col :cols="columnWidth" class="offset column-header">
        <span>{{ $t('map.deviceSettings.calibrationFactors.offset') }}</span>
      </v-col>
      <v-col v-if="showAutoCal" :cols="columnWidth">
        <div class="auto-cal-tooltip">
          <span class="auto-cal-column-header">
            {{ $t('map.deviceSettings.calibrationFactors.autoCal') }}
          </span>
          <v-tooltip location="bottom" max-width="600">
            <template v-slot:activator="{ props }">
              <span v-bind="props" icon>
                <v-icon data-cy="autoCalTooltip">mdi-information-outline</v-icon>
              </span>
            </template>
            <span>
              {{ $t('map.deviceSettings.calibrationFactors.autoCalTooltip') }}
            </span>
          </v-tooltip>
        </div>
      </v-col>
      <v-col v-if="showZeroing" :cols="columnWidth">
        <span class="zero-column-header" data-cy="zeroingColumnHeader">{{
          $t('map.deviceSettings.zeroing.zero')
        }}</span>
      </v-col>
    </v-row>

    <v-divider />

    <FusionScrollWrapper class="calibration-scroll" :padded="false" :height="300">
      <v-tooltip :open-on-click="true" location="bottom" :disabled="hasPermission">
        <template v-slot:activator="{ props }">
          <div v-bind="props" class="calibration-body">
            <v-row v-for="item in userCals" :key="item.name" align="center" data-cy="userCalRows">
              <v-col :cols="columnWidth">
                <div class="label">
                  <img :src="icons[item.name]" class="measurement-icon" alt="Measurement Icon" />
                  <span>{{ $t(`readings.${item.name}`) }}</span>
                </div>
              </v-col>
              <v-col :cols="columnWidth" class="multi-factor cell">
                <v-text-field
                  v-model="item.multiFactor"
                  :name="`multiplicationFactor-${item.name}`"
                  :disabled="item.multiFactor === undefined || !hasPermission"
                  :rules="multiFactorRules[item.name]"
                  :hide-details="true"
                  density="compact"
                  @focus="setHelpText(item.name, 'multiplicationFactor')"
                  @blur="clearHelpText"
                ></v-text-field>
              </v-col>
              <v-col :cols="columnWidth" class="offset cell">
                <v-text-field
                  v-model="item.offset"
                  :name="`offset-${item.name}`"
                  :disabled="item.offset === undefined || !hasPermission"
                  :rules="offsetRules[item.name]"
                  :hide-details="true"
                  density="compact"
                  @focus="setHelpText(item.name, 'offset')"
                  @blur="clearHelpText"
                ></v-text-field>
              </v-col>
              <v-col v-if="showAutoCal" :cols="columnWidth">
                <v-switch
                  v-if="sensorCanAutoCal(item.name)"
                  :disabled="!hasPermission"
                  v-model="item.ascCal"
                  color="primary"
                  :name="`ascCal-${item.name}`"
                  :hide-details="true"
                  density="compact"
                  class="auto-cal-switch"
                ></v-switch>
              </v-col>
              <v-col :cols="columnWidth" class="zero-cell" v-if="showZeroing">
                <v-tooltip location="bottom" v-if="sensorCanZero(item.name)">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon
                      variant="text"
                      size="x-small"
                      @click="requestZero(item.name, true)"
                      :class="getButtonStyles(item.name, true)"
                      :data-cy="`addUserZeroButton_${item.name}`"
                    >
                      <span class="zero-btn-overlay">0</span>
                      <v-icon>mdi-restore</v-icon>
                    </v-btn>
                  </template>
                  <span data-cy="zeroingTooltipText">
                    {{ getZeroButtonTooltipText(item.name) }}
                  </span>
                </v-tooltip>
                <v-tooltip location="bottom" v-if="showRemoveUserZeroButton(item.name)">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      @click="requestZero(item.name, false)"
                      icon
                      variant="text"
                      size="x-small"
                      :class="`${getButtonStyles(item.name, false)} request-factory-zero-btn`"
                      :data-cy="`removeUserZeroButton_${item.name}`"
                    >
                      <v-icon>mdi-close-circle-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('map.deviceSettings.zeroing.tooltip.userRemove') }}</span>
                </v-tooltip>
              </v-col>
            </v-row>
          </div>
        </template>
        <span>{{ $t('map.deviceSettings.calibrationFactors.upgradeTooltip') }}</span>
      </v-tooltip>
    </FusionScrollWrapper>

    <v-row v-if="helpText" :class="`calibration-footer ${calibrationFooterClass}`">
      <v-col cols="12">
        <p class="help-text">{{ helpText }}</p>
      </v-col>
    </v-row>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import { measurementIcon } from '@/services/measurement-icon'
import {
  AUTOCAL_SENSORS,
  DEFAULT_SLOPE,
  DEFAULT_OFFSET,
  type UserCalibration
} from '@/services/device-calibration'
import { ZEROING_SENSORS, ZeroState, type UserZero } from '@/services/device-zeroing'
import { SlugsEnum } from '@/permissions/SlugsEnum'
import { FeatureFlag } from '@/services/feature-flags'
import { NAMESPACES } from '@/store/types'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import type { DeviceFieldCalibration } from '@/types/devices'
import { FusionStores } from '@fusion/saas-components-v3'

const NUMERIC_REGEXES: Record<number, RegExp> = {
  0: /^-?\d{1,4}$/,
  1: /^-?\d{1,3}(\.\d)?$/,
  2: /^-?\d{1,3}(\.\d{1,2})?$/,
  3: /^-?\d{1,3}(\.\d{1,3})?$/
}

const DEFAULT_COLUMNS_NUMBER = 3
const DEFAULT_COLUMNS_WIDTH = 4

export default defineComponent({
  name: 'DeviceCalibration',
  props: {
    deviceModel: {
      type: String,
      required: true
    },
    userCals: {
      type: Array as PropType<UserCalibration[]>,
      required: true
    },
    permissions: {
      type: Array as PropType<string[]>,
      required: true
    },
    userZeros: {
      type: Object as PropType<Record<string, UserZero>>,
      required: true
    }
  },
  emits: {
    validated: (_payload: { valid: boolean; type: string }) => true,
    requestZero: (_sensorName: string, _zeroValue: boolean) => true,
    cancelZero: (_sensorName: string) => true
  },
  setup() {
    const featureFlagsStore = FusionStores.featureFlags()
    return { featureFlagsStore }
  },
  data() {
    return {
      rules: {
        required: (v: string): string | boolean =>
          v !== '' || (this.$t('validation.required') as string)
      },
      icons: measurementIcon,
      helpTextParams: {
        name: '',
        factor: ''
      },
      defaults: {
        multiplicationFactor: DEFAULT_SLOPE,
        offset: DEFAULT_OFFSET
      }
    }
  },
  watch: {
    userCals: {
      handler(newVal: UserCalibration[]) {
        this.validate(newVal)
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    showAutoCal() {
      return this.modelSupportsAutoCal
    },
    showZeroing() {
      return (
        (this.modelSupportsZeroing && this.enableZeroing) || this.deviceModel === '8145' // This is temporary, as we do not want 8145 included in the enableZeroing feature flag
      )
    },
    modelSupportsAutoCal() {
      return this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.MODEL_SUPPORTS_AUTO_CAL}`
      ](this.deviceModel)
    },
    modelSupportsZeroing() {
      return this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.MODEL_SUPPORTS_ZEROING}`
      ](this.deviceModel)
    },
    enableZeroing() {
      return this.featureFlagsStore.featureFlagBySlug(FeatureFlag.AirAssureZeroingEnabled)
    },
    numberOfColumns() {
      let columns = DEFAULT_COLUMNS_NUMBER
      if (this.showZeroing) columns++
      if (this.showAutoCal) columns++
      return columns
    },
    columnWidth() {
      return DEFAULT_COLUMNS_WIDTH - (this.numberOfColumns - DEFAULT_COLUMNS_NUMBER)
    },
    multiFactorRules(): Record<string, Array<(v: string) => string | boolean>> {
      const allRules: Record<string, Array<(v: string) => string | boolean>> = {}
      Object.entries(this.calibrationLimits as Record<string, DeviceFieldCalibration>).forEach(
        ([key, cal]) => {
          if (cal.multiplicationFactor) {
            const mfRule = (v: string): string | boolean => {
              return (
                (NUMERIC_REGEXES[cal.multiplicationFactor!.precision].test(v) &&
                  Number(v) >= cal.multiplicationFactor!.min &&
                  Number(v) <= cal.multiplicationFactor!.max) ||
                (this.$t('validation.invalidEntry') as string)
              )
            }
            allRules[key] = [this.rules.required, mfRule]
          } else {
            allRules[key] = []
          }
        }
      )
      return allRules
    },
    offsetRules(): Record<string, Array<(v: string) => string | boolean>> {
      const allRules: Record<string, Array<(v: string) => string | boolean>> = {}
      Object.entries(this.calibrationLimits as Record<string, DeviceFieldCalibration>).forEach(
        ([key, cal]) => {
          if (cal.offset) {
            const offsetRule = (v: string): string | boolean => {
              return (
                (NUMERIC_REGEXES[cal.offset!.precision].test(v) &&
                  Number(v) >= cal.offset!.min &&
                  Number(v) <= cal.offset!.max) ||
                (this.$t('validation.invalidEntry') as string)
              )
            }
            allRules[key] = [this.rules.required, offsetRule]
          } else {
            allRules[key] = []
          }
        }
      )
      return allRules
    },
    calibrationLimits(): Record<string, DeviceFieldCalibration> {
      return this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_CALIBRATION_LIMITS}`
      ](this.deviceModel)
    },
    hasPermission: function () {
      return this.permissions.includes(SlugsEnum.CalibrationWrite)
    },
    helpText() {
      let message = ''
      if (this.helpTextParams.name && this.helpTextParams.factor) {
        const { name, factor } = this.helpTextParams
        const limits = this.calibrationLimits[name]
        const typedFactor = factor as keyof DeviceFieldCalibration
        if (limits[typedFactor]) {
          const measurementName = this.$t(`readings.${name}`)
          const decimalPlacesText = this.$t(
            'map.deviceSettings.calibrationFactors.decimalPlaces',
            limits[typedFactor].precision
          )
          message = this.$t('map.deviceSettings.calibrationFactors.helpText', {
            measurement: measurementName,
            rangeLow: limits[typedFactor].min,
            rangeHigh: limits[typedFactor].max,
            decimalPlaces: decimalPlacesText,
            default: this.defaults[typedFactor]
          })
          const unitNoteKey = 'map.deviceSettings.calibrationFactors.helpTextOffsetNote.' + name
          if (this.$te(unitNoteKey) && typedFactor === 'offset') {
            message = `${message} ${this.$t(unitNoteKey)}`
          }
        }
      }
      return message
    },
    calibrationFooterClass() {
      return this.userCals?.length > 4 ? 'fixed-position' : ''
    }
  },
  methods: {
    validate(userCals: UserCalibration[]) {
      for (const uc of userCals) {
        const measurement = uc.name
        const mf = uc.multiFactor
        const offset = uc.offset
        const mfRules = this.multiFactorRules[measurement]
        const offsetRules = this.offsetRules[measurement]
        if (mfRules) {
          for (const rule of mfRules) {
            const msg = rule(String(mf))
            if (
              msg === this.$t('validation.invalidEntry') ||
              msg === this.$t('validation.required')
            ) {
              this.$emit('validated', { valid: false, type: 'calibration' })
              return false
            }
          }
        }
        if (offsetRules) {
          for (const rule of offsetRules) {
            const msg = rule(String(offset))
            if (
              msg === this.$t('validation.invalidEntry') ||
              msg === this.$t('validation.required')
            ) {
              this.$emit('validated', { valid: false, type: 'calibration' })
              return false
            }
          }
        }
      }
      this.$emit('validated', { valid: true, type: 'calibration' })
      return true
    },
    preventCalibrationFactor(event: KeyboardEvent) {
      // NOTE: may need to modify this to prevent input like "--4.323" or "0100..43" etc
      const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', '-', '.']
      if (allowedKeys.includes(event.key)) return
      if (!/^\d$/.test(event.key)) {
        event.preventDefault()
      }
    },
    navigateFaq() {
      const faqUrl = this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_FAQ_LINK}`
      ](this.deviceModel)
      if (faqUrl) {
        window.open(faqUrl)
      }
    },
    setHelpText(name: string, factor: string) {
      this.helpTextParams = {
        name,
        factor
      }
    },
    clearHelpText() {
      this.helpTextParams = {
        name: '',
        factor: ''
      }
    },
    requestZero(sensorName: string, zeroValue: boolean) {
      if (
        this.hasPermission &&
        !this.hasUserZeroRequestedState(sensorName) &&
        !this.hasFactoryZeroRequestedState(sensorName)
      ) {
        if (!this.hasUnsavedUserZero(sensorName) && !this.hasUnsavedFactoryZero(sensorName)) {
          this.$emit('requestZero', sensorName, zeroValue)
        } else {
          // if the user has already requested a zero, remove the request upon clicking icon again
          this.$emit('cancelZero', sensorName)
        }
      }
    },
    sensorCanZero(sensorName: string) {
      return (ZEROING_SENSORS as readonly string[]).includes(sensorName)
    },
    sensorCanAutoCal(sensorName: string) {
      return AUTOCAL_SENSORS.includes(sensorName)
    },
    hasUnsavedUserZero(sensorName: string) {
      return this.sensorCanZero(sensorName) && this.userZeros[sensorName]?.userzero === true
    },
    hasUnsavedFactoryZero(sensorName: string) {
      return this.sensorCanZero(sensorName) && this.userZeros[sensorName]?.userzero === false
    },
    hasUserZeroAppliedState(sensorName: string) {
      return (
        this.sensorCanZero(sensorName) &&
        this.userZeros[sensorName]?.zero_state === ZeroState.USER_ZERO_APPLIED
      )
    },
    hasUserZeroRequestedState(sensorName: string) {
      return (
        this.sensorCanZero(sensorName) &&
        this.userZeros[sensorName]?.zero_state === ZeroState.USER_ZERO_REQUESTED
      )
    },
    hasFactoryZeroAppliedState(sensorName: string) {
      return (
        this.sensorCanZero(sensorName) &&
        this.userZeros[sensorName]?.zero_state === ZeroState.FACTORY_ZERO_APPLIED
      )
    },
    hasFactoryZeroRequestedState(sensorName: string) {
      return (
        this.sensorCanZero(sensorName) &&
        this.userZeros[sensorName]?.zero_state === ZeroState.FACTORY_ZERO_REQUESTED
      )
    },
    getZeroAppliedAtString(sensorName: string) {
      if (this.sensorCanZero(sensorName)) {
        return new Date(this.userZeros[sensorName]?.zero_applied_at).toLocaleString()
      }
      return new Date('').toLocaleString()
    },
    getZeroRequestedAtString(sensorName: string) {
      if (this.sensorCanZero(sensorName)) {
        return new Date(this.userZeros[sensorName].zero_requested_at).toLocaleString()
      }
      return new Date('').toLocaleString()
    },
    getButtonStyles(sensorName: string, isUserZeroBtn: boolean) {
      if (isUserZeroBtn) {
        return `${this.getUserZeroButtonColor(
          sensorName
        )} ${this.getDisabledZeroButtonColor()} zero-btn`
      } else {
        return `${this.getDisabledZeroButtonColor()} zero-btn`
      }
    },
    getUserZeroButtonColor(sensorName: string) {
      if (this.hasUnsavedFactoryZero(sensorName) || this.hasFactoryZeroRequestedState(sensorName)) {
        return 'text-warning'
      } else if (
        this.hasUnsavedUserZero(sensorName) ||
        this.hasUserZeroRequestedState(sensorName)
      ) {
        return 'text-primary'
      } else if (this.hasUserZeroAppliedState(sensorName)) {
        return 'text-success'
      }
      return ''
    },
    getDisabledZeroButtonColor() {
      if (!this.hasPermission) {
        return 'disabled--color'
      }
      return ''
    },
    getZeroButtonTooltipText(sensorName: string) {
      if (this.hasUnsavedUserZero(sensorName)) {
        return this.$t('map.deviceSettings.zeroing.tooltip.userUnsaved')
      } else if (this.hasUnsavedFactoryZero(sensorName)) {
        return this.$t('map.deviceSettings.zeroing.tooltip.factoryUnsaved')
      } else if (this.hasUserZeroAppliedState(sensorName)) {
        return this.$t('map.deviceSettings.zeroing.tooltip.userApplied', {
          timestamp: this.getZeroAppliedAtString(sensorName)
        })
      } else if (this.hasUserZeroRequestedState(sensorName)) {
        return this.$t('map.deviceSettings.zeroing.tooltip.userRequested', {
          timestamp: this.getZeroRequestedAtString(sensorName)
        })
      } else if (this.hasFactoryZeroRequestedState(sensorName)) {
        return this.$t('map.deviceSettings.zeroing.tooltip.factoryRequested', {
          timestamp: this.getZeroRequestedAtString(sensorName)
        })
      } else if (this.hasFactoryZeroAppliedState(sensorName)) {
        if (this.userZeros[sensorName].zero_applied_at !== null) {
          return this.$t('map.deviceSettings.zeroing.tooltip.factoryApplied', {
            timestamp: this.getZeroAppliedAtString(sensorName)
          })
        }
        return this.$t('map.deviceSettings.zeroing.tooltip.factoryDefault')
      } else {
        return this.$t('map.deviceSettings.zeroing.tooltip.invalid')
      }
    },
    showRemoveUserZeroButton(sensorName: string) {
      return (
        this.sensorCanZero(sensorName) &&
        this.hasUserZeroAppliedState(sensorName) &&
        !this.hasUnsavedFactoryZero(sensorName) &&
        !this.hasUnsavedUserZero(sensorName)
      )
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/assets/global-mixins' as *;
@use '@/assets/global-variables' as *;

.v-dialog {
  .v-card {
    .v-input {
      padding: 0;
    }

    .v-text-field {
      padding: 0;
      margin-top: 4px;
      margin-bottom: 4px;
      :deep(.v-field__input) {
        font-size: 14px;
        padding: 4px 0 4px;
      }
    }

    .v-item-group .v-card {
      padding: 16px;
    }
  }

  .v-card.mobile {
    .calibration-card {
      .calibration-footer.fixed-position {
        position: fixed;
        bottom: 68px;
        right: 0;
        background: $white-color;
        padding: 16px;
        padding-top: 24px;
        margin: 0 16px;
      }

      .v-row {
        margin: 0 -6px;
      }

      .v-col-2 {
        padding: 0 6px;
        min-width: 52px;

        @media (max-width: 430px) {
          &.multi-factor,
          &.offset {
            max-width: min-content;
          }
        }
      }

      .auto-cal-tooltip {
        .v-icon {
          margin-left: 1px;
        }
      }
    }
  }

  .v-card-title.tab-content__title {
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    padding: 0;
    opacity: 0.56;
  }

  .v-card-subtitle {
    position: relative;
    padding: 0 0 16px 0;
    color: rgba(0, 0, 0, 0.9);
    opacity: 1;
  }

  .v-btn {
    min-width: 120px !important;

    @media (min-width: 496px) {
      min-width: 160px !important;
    }
  }

  .zero-btn-overlay {
    font-size: 14px;
    position: absolute;
    right: 9.5px;
  }

  .zero-btn {
    font-size: 16px;
    min-width: 0px !important;
  }

  .zero-btn.disabled--color {
    opacity: 0.56;
  }
}

.calibration-header {
  position: relative;
  padding-right: 16px;
  margin-top: 0;
  margin-bottom: 8px;

  .v-col {
    span {
      display: inline-block;
      font-size: 0.75rem;
      line-height: 2;
      font-weight: 700;
      color: rgba(0, 0, 0, 0.6);
    }
  }

  .auto-cal-tooltip {
    display: flex;
    align-items: center;
    .v-icon {
      margin-left: 3px;
    }
  }
}

.calibration-scroll {
  padding-right: 0;
  margin-top: 4px;
}

.calibration-body {
  .v-row:last-child {
    margin-bottom: 0;
  }

  :deep(.v-selection-control__wrapper) {
    @include center-absolute-element;
  }
}

.calibration-footer {
  margin-top: 16px;
  margin-bottom: 0;
  padding-top: 16px;
}

.v-row {
  margin-top: 0;
  margin-bottom: 0;
}

.v-col {
  padding-top: 0;
  padding-bottom: 0;
}

.v-col-2 {
  max-width: 100%;
  flex-grow: 1 !important;

  .request-factory-zero-btn {
    margin-left: -4px;
  }
}

.label {
  font-size: 13px;
  display: flex;

  img {
    width: 17px;
    height: 17px;
    margin-top: 1px;
    margin-right: 3px;
  }
}

.help-text {
  font-size: 0.8125rem;
  line-height: 1.375;
  height: calc(1.375em * 3);
  margin-top: 0;
  margin-bottom: 0;

  .v-dialog--fullscreen & {
    height: calc(1.375em * 4);
  }
}

.auto-cal-column-header {
  white-space: nowrap;
}

.zero-column-header {
  margin-left: 8px;
}

.zero-cell {
  display: flex;
  padding-left: 16px;
  padding-right: 16px;
}
</style>
